import { DrawerActions, NavigationProp } from '@react-navigation/core';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect
} from 'react';
import { Alert, Animated, Platform } from 'react-native';
import { FetchError } from './api/api';
import AppManifest from './common/AppManifest';
import environment from '../src/environments';
import { setupAppcues } from './common/appcues';
import { log } from './tools/LogHelper';
import { stringify } from 'flatted';

export interface StudyHeaderConfig {
  title?: string;
  caption?: string;
  backgroundSource?: string;
  foregroundColor?: string;
}

export interface AppContextProps {
  headerConfig?: StudyHeaderConfig;
  scrollY: Animated.Value;
  setHeaderConfig: React.Dispatch<
    React.SetStateAction<StudyHeaderConfig | undefined>
  >;
  // setScrollY: React.Dispatch<React.SetStateAction<number>>;
  openDrawer: (navigation: NavigationProp<any>) => void;
  logError: (message: string, error: Error | FetchError) => void;
}

export const AppContext = createContext<AppContextProps>(null!);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error(
      'useAppContext must be used within the context of the AppProvider component'
    );
  }
  return context;
};

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [headerConfig, setHeaderConfig] = useState<StudyHeaderConfig>();
  const scrollY = useRef(new Animated.Value(0)).current;

  const openDrawer = useCallback((navigation: NavigationProp<any>) => {
    navigation.dispatch(DrawerActions.openDrawer());
  }, []);

  const logError = useCallback((message: string, error: Error | FetchError) => {
    // TODO: log errors to crashlytics
    let errorMessage = error.message;
    if (Array.isArray(errorMessage)) {
      errorMessage = (error.message as any).join(', ');
    }
    if (AppManifest.env !== 'development') {
      // don't alert for non-development environments
      return;
    }
    if (Platform.OS === 'web') {
      alert(`${message}: ${errorMessage}`);
    } else {
      Alert.alert(message, errorMessage, [{ text: 'Ok' }]);
    }
    log('Error', stringify(error));
  }, []);

  // Setup appcues
  useEffect(() => {
    (async () => {
      if (environment.appcuesAppId && environment.appcuesAccountId) {
        // Setup Appcues using Appcues AccountId and AppId
        await setupAppcues(
          environment.appcuesAccountId,
          environment.appcuesAppId
        );
      }
    })();
  }, []);

  return (
    <AppContext.Provider
      value={{
        scrollY,
        headerConfig,
        setHeaderConfig,
        openDrawer,
        logError
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
